import React, { useState, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTintedBackground } from '../../hooks/useTintedBackground';
import { ZoomControls } from './toolbar/ZoomControls';
import { LayoutControls } from './toolbar/LayoutControls';
import { NodeBrowser } from './toolbar/NodeBrowser';
import { BulkOperations } from './toolbar/BulkOperations';
import { ChatWithCanvas } from './ChatWithCanvas';
import { FiMessageSquare } from 'react-icons/fi';

interface GraphToolbarProps {
  // Layout controls
  layoutMode: 'default' | 'hierarchical' | 'circular' | 'force' | 'grid';
  onLayoutModeChange: (mode: 'default' | 'hierarchical' | 'circular' | 'force' | 'grid') => void;
  
  // Connector controls
  connectorType: 'curved' | 'straight' | 'smoothstep' | 'step';
  onConnectorTypeChange: (type: 'curved' | 'straight' | 'smoothstep' | 'step') => void;
  
  // Selection and bulk operations
  selectedCount: number;
  onClearSelection: () => void;
  onSelectAll: () => void;
  onInvertSelection: () => void;
  onBulkAlignLeft?: () => void;
  onBulkAlignTop?: () => void;
  onBulkDistributeHorizontally?: () => void;
  onBulkDistributeVertically?: () => void;
  onBulkDelete?: () => void;
  
  // Search functionality
  nodes?: any[];
  onFocusNode?: (nodeId: string) => void;
  onNodesChange?: (changes: any[]) => void;
  
  // Layout options
  layout?: 'compact' | 'full' | 'minimal';
  position?: 'top' | 'bottom' | 'left' | 'right';
  
  // Chat integration (COPIED FROM PersonaForge pattern)
  workflowData?: any;
  onWorkflowChange?: (change: any) => void;
  mode?: 'view' | 'edit' | 'execute';
}

export const GraphToolbar: React.FC<GraphToolbarProps> = ({
  layoutMode,
  onLayoutModeChange,
  connectorType,
  onConnectorTypeChange,
  selectedCount,
  onClearSelection,
  onSelectAll,
  onInvertSelection,
  onBulkAlignLeft,
  onBulkAlignTop,
  onBulkDistributeHorizontally,
  onBulkDistributeVertically,
  onBulkDelete,
  nodes = [],
  onFocusNode,
  onNodesChange,
  layout = 'full',
  position = 'top',
  workflowData,
  onWorkflowChange,
  mode = 'view'
}) => {
  const { darkMode } = useTheme();
  
  // Chat state (COPIED FROM PersonaForge pattern)
  const [showChat, setShowChat] = useState(false);

  // Add tinted background effect
  const tintedBg = useTintedBackground({
    tintColor: darkMode ? '#10b981' : '#059669',
    enabled: true
  });

  // Get selected nodes from ReactFlow's native selection
  const selectedNodes = nodes.filter(node => node.selected);
  
  // Handle workflow updates from chat with comprehensive logging
  const handleWorkflowUpdate = useCallback((updatedWorkflow: any) => {
    console.log('🔄 WORKFLOW-DEBUG: handleWorkflowUpdate called with:', {
      updatedWorkflowId: updatedWorkflow?.id,
      updatedWorkflowName: updatedWorkflow?.name,
      phasesCount: updatedWorkflow?.phases?.length,
      nodesCount: updatedWorkflow?.nodes?.length,
      connectionsCount: updatedWorkflow?.connections?.length,
      hasOnWorkflowChange: !!onWorkflowChange
    });
    
    if (onWorkflowChange) {
      console.log('🔄 WORKFLOW-DEBUG: Calling onWorkflowChange with replace_workflow');
      onWorkflowChange({
        type: 'replace_workflow',
        workflow: updatedWorkflow
      });
    } else {
      console.warn('🔄 WORKFLOW-DEBUG: onWorkflowChange is not available!');
    }
  }, [onWorkflowChange]);

  // Compact layout - essential controls only
  if (layout === 'compact') {
    return (
      <div className={`flex items-center space-x-2 p-2 rounded border ${
        darkMode 
          ? 'bg-gray-800/90 border-green-700/50 backdrop-blur-sm' 
          : 'bg-white/90 border-green-300/50 backdrop-blur-sm'
      } shadow-lg`}>
        <ZoomControls layout="compact" />
        <LayoutControls
          layoutMode={layoutMode}
          onLayoutModeChange={onLayoutModeChange}
          connectorType={connectorType}
          onConnectorTypeChange={onConnectorTypeChange}
          layout="compact"
        />
      </div>
    );
  }

  // Minimal layout - just zoom controls
  if (layout === 'minimal') {
    return (
      <div className={`flex items-center space-x-1 p-1 rounded ${
        darkMode ? 'bg-gray-800/80' : 'bg-white/80'
      } backdrop-blur-sm`}>
        <ZoomControls layout="minimal" />
      </div>
    );
  }

  // Full layout
  return (
    <div 
      ref={tintedBg.ref}
      className={`flex flex-col border rounded-xl shadow-xl transition-all duration-300 ${
        darkMode 
          ? 'border-green-700/30 shadow-green-500/10' 
          : 'border-green-200/30 shadow-green-300/20'
      }`}
      style={{
        ...tintedBg.style,
        backgroundColor: darkMode ? 'rgba(17, 24, 39, 0.1)' : 'rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Dynamic zoom overlay following cursor */}
      <div
        className="absolute inset-0 pointer-events-none rounded-xl"
        style={tintedBg.overlayStyle}
      />
      
      {/* First row: Zoom + Layout - Compact and sleek */}
      <div className="flex items-center justify-between px-3 py-1.5 space-x-3">
        {/* Left: Zoom controls with glow effect */}
        <div className="flex items-center space-x-1">
          <ZoomControls layout="full" />
        </div>

        {/* Center: Spacer with subtle gradient */}
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-green-300/20 to-transparent"></div>

        {/* Right: Layout + Connectors with modern styling */}
        <div className="flex items-center space-x-1">
          <LayoutControls
            layoutMode={layoutMode}
            onLayoutModeChange={onLayoutModeChange}
            connectorType={connectorType}
            onConnectorTypeChange={onConnectorTypeChange}
            layout="full"
          />
        </div>
      </div>

      {/* Second row: Selection browser - Compact when visible */}
      {selectedCount > 0 && (
        <div className={`px-3 py-1 border-t transition-all duration-200 ${
          darkMode ? 'border-gray-700/50 bg-blue-900/10' : 'border-gray-200/50 bg-blue-50/30'
        }`}>
          <NodeBrowser
            nodes={nodes}
            selectedNodes={selectedNodes}
            onFocusNode={onFocusNode}
            onNodesChange={onNodesChange}
            onClearSelection={onClearSelection}
            onSelectAll={onSelectAll}
          />
        </div>
      )}

      {/* Third row: Alignment operations - Slides down smoothly */}
      <div className={`overflow-hidden transition-all duration-300 ease-out ${
        selectedCount > 1 ? 'max-h-12 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className={`px-3 py-1.5 border-t ${
          darkMode ? 'border-gray-700/50 bg-gradient-to-r from-orange-900/10 to-purple-900/10' : 'border-gray-200/50 bg-gradient-to-r from-orange-50/50 to-purple-50/50'
        }`}>
          <div className="flex items-center justify-between space-x-2">
            {/* Left: Alignment controls - Compact buttons */}
            <div className="flex items-center space-x-1">
              <span className="text-xs text-gray-500 font-medium">Align:</span>
              <button 
                onClick={onBulkAlignLeft} 
                className={`px-1.5 py-0.5 text-xs rounded-md transition-all duration-200 hover:scale-105 ${
                  darkMode 
                    ? 'bg-orange-700/80 hover:bg-orange-600 text-white shadow-orange-500/20 hover:shadow-md' 
                    : 'bg-orange-500 hover:bg-orange-600 text-white shadow-orange-300/30 hover:shadow-md'
                }`} 
                title="Align Left"
              >
                ⫷
              </button>
              <button 
                onClick={onBulkAlignTop} 
                className={`px-1.5 py-0.5 text-xs rounded-md transition-all duration-200 hover:scale-105 ${
                  darkMode 
                    ? 'bg-orange-700/80 hover:bg-orange-600 text-white shadow-orange-500/20 hover:shadow-md' 
                    : 'bg-orange-500 hover:bg-orange-600 text-white shadow-orange-300/30 hover:shadow-md'
                }`} 
                title="Align Top"
              >
                ⫸
              </button>
            </div>

            {/* Center: Distribution controls - Only when 3+ selected */}
            {selectedCount > 2 && (
              <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-500 font-medium">Distribute:</span>
                <button 
                  onClick={onBulkDistributeHorizontally} 
                  className={`px-1.5 py-0.5 text-xs rounded-md transition-all duration-200 hover:scale-105 ${
                    darkMode 
                      ? 'bg-purple-700/80 hover:bg-purple-600 text-white shadow-purple-500/20 hover:shadow-md' 
                      : 'bg-purple-500 hover:bg-purple-600 text-white shadow-purple-300/30 hover:shadow-md'
                  }`} 
                  title="Distribute Horizontally"
                >
                  ⟷
                </button>
                <button 
                  onClick={onBulkDistributeVertically} 
                  className={`px-1.5 py-0.5 text-xs rounded-md transition-all duration-200 hover:scale-105 ${
                    darkMode 
                      ? 'bg-purple-700/80 hover:bg-purple-600 text-white shadow-purple-500/20 hover:shadow-md' 
                      : 'bg-purple-500 hover:bg-purple-600 text-white shadow-purple-300/30 hover:shadow-md'
                  }`} 
                  title="Distribute Vertically"
                >
                  ⟦
                </button>
              </div>
            )}

            {/* Right: Selection counter with pulse animation */}
            <div className="flex items-center">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium transition-all duration-200 ${
                darkMode 
                  ? 'bg-blue-800/60 text-blue-200 border border-blue-600/30' 
                  : 'bg-blue-100/80 text-blue-800 border border-blue-200/50'
              } animate-pulse`}>
                {selectedCount}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
